/* -------------------------------------------------------------------------- */
/*  UX-48 — Regional / City Landing Pages: Data Layer                         */
/*  All 50 US states + 50 metro areas with tax, fee, and delivery data        */
/* -------------------------------------------------------------------------- */

export interface StateData {
  slug: string;
  name: string;
  abbreviation: string;
  salesTaxRate: number; // percentage
  registrationFee: number; // cents
  titleFee: number; // cents
  emissionsTest: boolean;
  safetyInspection: boolean;
  deliveryDays: { min: number; max: number };
  popularMakes: string[];
  popularBodyTypes: string[];
  vehicleCount: number;
  cities: CityData[];
}

export interface CityData {
  slug: string;
  name: string;
  stateSlug: string;
  stateAbbreviation: string;
  metroPopulation: number;
  deliveryDays: { min: number; max: number };
  popularMakes: string[];
  vehicleCount: number;
  nearestHub: string;
  hubDistance: number; // miles
  localTaxAddon: number; // additional local tax percentage
  description: string; // 60-80 word editorial about car buying in this city
  seo: { title: string; description: string };
}

/* -------------------------------------------------------------------------- */
/*  Helper: format cents to dollars                                           */
/* -------------------------------------------------------------------------- */

export function formatFeeCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/* -------------------------------------------------------------------------- */
/*  State + City data                                                         */
/* -------------------------------------------------------------------------- */

export const states: StateData[] = [
  /* ---- Alabama ---- */
  {
    slug: 'alabama',
    name: 'Alabama',
    abbreviation: 'AL',
    salesTaxRate: 2,
    registrationFee: 2300,
    titleFee: 1800,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Honda', 'Nissan'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 1842,
    cities: [
      {
        slug: 'birmingham',
        name: 'Birmingham',
        stateSlug: 'alabama',
        stateAbbreviation: 'AL',
        metroPopulation: 1115000,
        deliveryDays: { min: 5, max: 8 },
        popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'BMW', 'Mercedes-Benz'],
        vehicleCount: 924,
        nearestHub: 'Atlanta Hub',
        hubDistance: 147,
        localTaxAddon: 4,
        description:
          'Birmingham is the cultural and commercial heart of Alabama, where Southern charm meets modern sophistication. The city\u2019s revitalized downtown and growing tech sector have fueled demand for premium pre-owned SUVs and sedans. Buyers here value reliability and value, making certified vehicles the preferred choice among discerning professionals navigating the Magic City\u2019s expanding metro.',
        seo: {
          title: 'Used Cars in Birmingham, AL | BESPOKIAN Auto',
          description:
            'Browse 924 certified pre-owned vehicles available for delivery to Birmingham, Alabama. Every car inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Alaska ---- */
  {
    slug: 'alaska',
    name: 'Alaska',
    abbreviation: 'AK',
    salesTaxRate: 0,
    registrationFee: 10000,
    titleFee: 1500,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 10, max: 16 },
    popularMakes: ['Toyota', 'Subaru', 'Ford', 'Chevrolet', 'Jeep'],
    popularBodyTypes: ['SUV', 'Pickup Truck', 'Crossover'],
    vehicleCount: 487,
    cities: [
      {
        slug: 'anchorage',
        name: 'Anchorage',
        stateSlug: 'alaska',
        stateAbbreviation: 'AK',
        metroPopulation: 396000,
        deliveryDays: { min: 10, max: 15 },
        popularMakes: ['Toyota', 'Subaru', 'Ford', 'Jeep', 'Chevrolet'],
        vehicleCount: 312,
        nearestHub: 'Seattle Hub',
        hubDistance: 2350,
        localTaxAddon: 0,
        description:
          'Anchorage buyers demand rugged, all-weather vehicles built for the Last Frontier. Four-wheel-drive SUVs and trucks dominate the market, with Toyota and Subaru leading reliability rankings through brutal winters. The city\u2019s unique geography makes certified pre-owned an intelligent choice, ensuring every vehicle has been thoroughly inspected for the demanding conditions of Alaskan roads and climate.',
        seo: {
          title: 'Used Cars in Anchorage, AK | BESPOKIAN Auto',
          description:
            'Browse 312 certified pre-owned vehicles for delivery to Anchorage, Alaska. AWD-ready SUVs and trucks inspected for Last Frontier conditions.',
        },
      },
    ],
  },
  /* ---- Arizona ---- */
  {
    slug: 'arizona',
    name: 'Arizona',
    abbreviation: 'AZ',
    salesTaxRate: 5.6,
    registrationFee: 50000,
    titleFee: 400,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Toyota', 'Ford', 'Chevrolet', 'Honda', 'Ram'],
    popularBodyTypes: ['SUV', 'Pickup Truck', 'Sedan'],
    vehicleCount: 3156,
    cities: [
      {
        slug: 'phoenix',
        name: 'Phoenix',
        stateSlug: 'arizona',
        stateAbbreviation: 'AZ',
        metroPopulation: 4946000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Toyota', 'Ford', 'Honda', 'Chevrolet', 'Tesla'],
        vehicleCount: 2104,
        nearestHub: 'Phoenix Hub',
        hubDistance: 0,
        localTaxAddon: 2.3,
        description:
          'Phoenix is one of America\u2019s fastest-growing metros, and its sun-drenched climate produces exceptionally well-preserved vehicles. The Valley of the Sun\u2019s sprawling layout makes personal transportation essential, driving strong demand for fuel-efficient SUVs and premium sedans. With minimal rust concerns and year-round driving conditions, certified pre-owned vehicles from Phoenix represent outstanding long-term value for buyers.',
        seo: {
          title: 'Used Cars in Phoenix, AZ | BESPOKIAN Auto',
          description:
            'Browse 2,104 certified pre-owned vehicles in Phoenix, Arizona. Sun-preserved quality, inspected and delivered with our 100-Day Guarantee.',
        },
      },
      {
        slug: 'tucson',
        name: 'Tucson',
        stateSlug: 'arizona',
        stateAbbreviation: 'AZ',
        metroPopulation: 1043000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Toyota', 'Honda', 'Ford', 'Subaru', 'Hyundai'],
        vehicleCount: 687,
        nearestHub: 'Phoenix Hub',
        hubDistance: 112,
        localTaxAddon: 2.6,
        description:
          'Tucson\u2019s university culture and desert landscape create a distinctive automotive market favoring practical crossovers and fuel-efficient sedans. The Old Pueblo\u2019s growing tech corridor attracts younger professionals seeking reliable certified pre-owned vehicles at competitive prices. Dry Sonoran air means vehicles here age gracefully, with minimal corrosion, making Tucson an excellent market for premium pre-owned inventory.',
        seo: {
          title: 'Used Cars in Tucson, AZ | BESPOKIAN Auto',
          description:
            'Browse 687 certified pre-owned vehicles available in Tucson, Arizona. Desert-preserved quality backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Arkansas ---- */
  {
    slug: 'arkansas',
    name: 'Arkansas',
    abbreviation: 'AR',
    salesTaxRate: 6.5,
    registrationFee: 1700,
    titleFee: 1000,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Ram', 'Honda'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 968,
    cities: [],
  },
  /* ---- California ---- */
  {
    slug: 'california',
    name: 'California',
    abbreviation: 'CA',
    salesTaxRate: 7.25,
    registrationFee: 6000,
    titleFee: 2300,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Tesla', 'Toyota', 'Honda', 'BMW', 'Mercedes-Benz'],
    popularBodyTypes: ['SUV', 'Sedan', 'Electric', 'Crossover'],
    vehicleCount: 8742,
    cities: [
      {
        slug: 'los-angeles',
        name: 'Los Angeles',
        stateSlug: 'california',
        stateAbbreviation: 'CA',
        metroPopulation: 13201000,
        deliveryDays: { min: 2, max: 5 },
        popularMakes: ['Tesla', 'BMW', 'Mercedes-Benz', 'Toyota', 'Porsche'],
        vehicleCount: 3845,
        nearestHub: 'Los Angeles Hub',
        hubDistance: 0,
        localTaxAddon: 2.25,
        description:
          'Los Angeles defines American car culture, from its iconic freeways to its appetite for luxury and innovation. The sprawling metro\u2019s love affair with premium vehicles makes it the nation\u2019s largest market for certified pre-owned luxury cars. Tesla, BMW, and Mercedes-Benz dominate, reflecting LA\u2019s forward-thinking embrace of both electric performance and timeless European craftsmanship.',
        seo: {
          title: 'Used Cars in Los Angeles, CA | BESPOKIAN Auto',
          description:
            'Browse 3,845 certified pre-owned luxury vehicles in Los Angeles. From Tesla to BMW, every car inspected and backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'san-francisco',
        name: 'San Francisco',
        stateSlug: 'california',
        stateAbbreviation: 'CA',
        metroPopulation: 4738000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Tesla', 'Toyota', 'BMW', 'Audi', 'Rivian'],
        vehicleCount: 2198,
        nearestHub: 'San Francisco Hub',
        hubDistance: 0,
        localTaxAddon: 1.25,
        description:
          'San Francisco\u2019s tech-forward culture has made it the epicenter of electric and luxury pre-owned vehicles. Bay Area buyers prioritize innovation, sustainability, and premium features, driving exceptional demand for Tesla, Rivian, and Audi. The city\u2019s compact geography and excellent transit mean residents choose their vehicles deliberately, favoring quality and certified assurance over quantity.',
        seo: {
          title: 'Used Cars in San Francisco, CA | BESPOKIAN Auto',
          description:
            'Browse 2,198 certified pre-owned vehicles in San Francisco. Tech-forward luxury from Tesla to Audi, backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'san-diego',
        name: 'San Diego',
        stateSlug: 'california',
        stateAbbreviation: 'CA',
        metroPopulation: 3338000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Toyota', 'Honda', 'Tesla', 'BMW', 'Ford'],
        vehicleCount: 1612,
        nearestHub: 'Los Angeles Hub',
        hubDistance: 120,
        localTaxAddon: 0.5,
        description:
          'San Diego\u2019s perfect climate and outdoor lifestyle create demand for versatile SUVs and convertibles that thrive in year-round sunshine. Military families and tech professionals form a discerning buyer base that values certified reliability and transparent pricing. The coastal city\u2019s relaxed pace extends to its automotive market, where buyers take time to find the right vehicle with confidence.',
        seo: {
          title: 'Used Cars in San Diego, CA | BESPOKIAN Auto',
          description:
            'Browse 1,612 certified pre-owned vehicles in San Diego. Sun-preserved quality inspected and delivered with our 100-Day Guarantee.',
        },
      },
      {
        slug: 'sacramento',
        name: 'Sacramento',
        stateSlug: 'california',
        stateAbbreviation: 'CA',
        metroPopulation: 2364000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Toyota', 'Honda', 'Ford', 'Tesla', 'Chevrolet'],
        vehicleCount: 1087,
        nearestHub: 'San Francisco Hub',
        hubDistance: 88,
        localTaxAddon: 0.75,
        description:
          'Sacramento\u2019s role as California\u2019s capital and its booming suburban growth drive strong demand for family-friendly SUVs and efficient commuter sedans. As the gateway to the Sierra Nevada, residents also favor capable all-wheel-drive crossovers for weekend mountain adventures. The city\u2019s more affordable cost of living compared to coastal metros makes certified pre-owned vehicles an especially compelling value proposition.',
        seo: {
          title: 'Used Cars in Sacramento, CA | BESPOKIAN Auto',
          description:
            'Browse 1,087 certified pre-owned vehicles in Sacramento. Every vehicle inspected and delivered with our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Colorado ---- */
  {
    slug: 'colorado',
    name: 'Colorado',
    abbreviation: 'CO',
    salesTaxRate: 2.9,
    registrationFee: 5000,
    titleFee: 750,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Subaru', 'Toyota', 'Jeep', 'Ford', 'Tesla'],
    popularBodyTypes: ['SUV', 'Crossover', 'Pickup Truck'],
    vehicleCount: 2874,
    cities: [
      {
        slug: 'denver',
        name: 'Denver',
        stateSlug: 'colorado',
        stateAbbreviation: 'CO',
        metroPopulation: 2963000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Subaru', 'Toyota', 'Tesla', 'Jeep', 'Audi'],
        vehicleCount: 1956,
        nearestHub: 'Denver Hub',
        hubDistance: 0,
        localTaxAddon: 4.81,
        description:
          'Denver\u2019s active, outdoor-oriented culture drives an automotive market uniquely focused on all-wheel-drive capability and adventure-ready versatility. Subaru practically owns the Mile High City, while Tesla\u2019s growing presence reflects Colorado\u2019s sustainability values. Mountain-bound weekends demand vehicles that handle I-70 snow with confidence, making certified pre-owned AWD crossovers the most sought-after segment in this dynamic metro.',
        seo: {
          title: 'Used Cars in Denver, CO | BESPOKIAN Auto',
          description:
            'Browse 1,956 certified pre-owned vehicles in Denver. AWD-ready SUVs and crossovers inspected for mountain driving, backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Connecticut ---- */
  {
    slug: 'connecticut',
    name: 'Connecticut',
    abbreviation: 'CT',
    salesTaxRate: 6.35,
    registrationFee: 12000,
    titleFee: 2500,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Toyota', 'Honda', 'BMW', 'Mercedes-Benz', 'Audi'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 1654,
    cities: [],
  },
  /* ---- Delaware ---- */
  {
    slug: 'delaware',
    name: 'Delaware',
    abbreviation: 'DE',
    salesTaxRate: 0,
    registrationFee: 4000,
    titleFee: 3500,
    emissionsTest: false,
    safetyInspection: true,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW'],
    popularBodyTypes: ['Sedan', 'SUV', 'Crossover'],
    vehicleCount: 534,
    cities: [],
  },
  /* ---- Florida ---- */
  {
    slug: 'florida',
    name: 'Florida',
    abbreviation: 'FL',
    salesTaxRate: 6,
    registrationFee: 22500,
    titleFee: 7535,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Toyota', 'Honda', 'Ford', 'BMW', 'Mercedes-Benz'],
    popularBodyTypes: ['SUV', 'Sedan', 'Convertible', 'Pickup Truck'],
    vehicleCount: 6524,
    cities: [
      {
        slug: 'miami',
        name: 'Miami',
        stateSlug: 'florida',
        stateAbbreviation: 'FL',
        metroPopulation: 6139000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Mercedes-Benz', 'BMW', 'Tesla', 'Porsche', 'Audi'],
        vehicleCount: 2487,
        nearestHub: 'Miami Hub',
        hubDistance: 0,
        localTaxAddon: 1,
        description:
          'Miami\u2019s glamorous lifestyle and international flair create one of America\u2019s most exciting luxury car markets. From Brickell\u2019s gleaming towers to South Beach\u2019s Ocean Drive, premium European brands and high-performance vehicles define the city\u2019s automotive identity. Latin American influence adds appetite for bold design, while year-round warmth keeps convertibles and sport coupes in constant demand among Miami\u2019s discerning buyers.',
        seo: {
          title: 'Used Cars in Miami, FL | BESPOKIAN Auto',
          description:
            'Browse 2,487 certified pre-owned luxury vehicles in Miami. Premium European brands inspected and delivered with our 100-Day Guarantee.',
        },
      },
      {
        slug: 'tampa',
        name: 'Tampa',
        stateSlug: 'florida',
        stateAbbreviation: 'FL',
        metroPopulation: 3219000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Toyota', 'Ford', 'Honda', 'Chevrolet', 'BMW'],
        vehicleCount: 1524,
        nearestHub: 'Miami Hub',
        hubDistance: 280,
        localTaxAddon: 1.5,
        description:
          'Tampa Bay\u2019s rapid growth and diverse economy have created a robust market for certified pre-owned vehicles across all segments. Young professionals flocking to the area seek value-conscious luxury, while established families favor spacious SUVs for Florida\u2019s outdoor lifestyle. The bay area\u2019s moderate insurance rates and salt-air-free conditions inland make this an ideal market for premium pre-owned vehicles.',
        seo: {
          title: 'Used Cars in Tampa, FL | BESPOKIAN Auto',
          description:
            'Browse 1,524 certified pre-owned vehicles in Tampa Bay. Every vehicle inspected and backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'orlando',
        name: 'Orlando',
        stateSlug: 'florida',
        stateAbbreviation: 'FL',
        metroPopulation: 2674000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Toyota', 'Honda', 'Ford', 'Nissan', 'Hyundai'],
        vehicleCount: 1342,
        nearestHub: 'Miami Hub',
        hubDistance: 235,
        localTaxAddon: 0.5,
        description:
          'Orlando\u2019s tourism-driven economy and explosive suburban growth fuel demand for reliable family vehicles and efficient commuter cars. As Central Florida\u2019s hub, residents navigate significant daily distances, prioritizing fuel efficiency and dependability. The city\u2019s young, diverse population embraces certified pre-owned as a smart path to quality transportation, with SUVs and crossovers leading the market.',
        seo: {
          title: 'Used Cars in Orlando, FL | BESPOKIAN Auto',
          description:
            'Browse 1,342 certified pre-owned vehicles in Orlando. Family-friendly SUVs and sedans, inspected and backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'jacksonville',
        name: 'Jacksonville',
        stateSlug: 'florida',
        stateAbbreviation: 'FL',
        metroPopulation: 1605000,
        deliveryDays: { min: 5, max: 8 },
        popularMakes: ['Ford', 'Toyota', 'Chevrolet', 'Honda', 'Ram'],
        vehicleCount: 1171,
        nearestHub: 'Miami Hub',
        hubDistance: 347,
        localTaxAddon: 0.5,
        description:
          'Jacksonville\u2019s sprawling geography and military heritage shape an automotive market that favors trucks, SUVs, and practical vehicles built for long commutes. As Florida\u2019s largest city by area, reliable transportation is essential. Naval Station Mayport and multiple military installations create a steady stream of buyers who appreciate the transparency and certification that BESPOKIAN provides.',
        seo: {
          title: 'Used Cars in Jacksonville, FL | BESPOKIAN Auto',
          description:
            'Browse 1,171 certified pre-owned vehicles in Jacksonville. Trucks and SUVs inspected and delivered with our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Georgia ---- */
  {
    slug: 'georgia',
    name: 'Georgia',
    abbreviation: 'GA',
    salesTaxRate: 4,
    registrationFee: 2000,
    titleFee: 1800,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Ford', 'Toyota', 'Honda', 'Chevrolet', 'BMW'],
    popularBodyTypes: ['SUV', 'Sedan', 'Pickup Truck'],
    vehicleCount: 4218,
    cities: [
      {
        slug: 'atlanta',
        name: 'Atlanta',
        stateSlug: 'georgia',
        stateAbbreviation: 'GA',
        metroPopulation: 6089000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['BMW', 'Mercedes-Benz', 'Toyota', 'Honda', 'Tesla'],
        vehicleCount: 2856,
        nearestHub: 'Atlanta Hub',
        hubDistance: 0,
        localTaxAddon: 3.9,
        description:
          'Atlanta is the economic engine of the Southeast, where Southern hospitality meets cosmopolitan ambition in one of America\u2019s most dynamic car markets. The city\u2019s legendary traffic makes comfortable, well-equipped vehicles a priority. German luxury brands thrive here alongside Japanese reliability champions, reflecting a diverse buyer base that demands both prestige and practical excellence from their pre-owned vehicles.',
        seo: {
          title: 'Used Cars in Atlanta, GA | BESPOKIAN Auto',
          description:
            'Browse 2,856 certified pre-owned vehicles in Atlanta. Premium brands inspected and delivered with our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Hawaii ---- */
  {
    slug: 'hawaii',
    name: 'Hawaii',
    abbreviation: 'HI',
    salesTaxRate: 4,
    registrationFee: 4500,
    titleFee: 500,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 12, max: 18 },
    popularMakes: ['Toyota', 'Honda', 'Subaru', 'Nissan', 'Jeep'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 398,
    cities: [
      {
        slug: 'honolulu',
        name: 'Honolulu',
        stateSlug: 'hawaii',
        stateAbbreviation: 'HI',
        metroPopulation: 1016000,
        deliveryDays: { min: 12, max: 17 },
        popularMakes: ['Toyota', 'Honda', 'Subaru', 'Nissan', 'Tesla'],
        vehicleCount: 287,
        nearestHub: 'Los Angeles Hub',
        hubDistance: 2471,
        localTaxAddon: 0.5,
        description:
          'Honolulu\u2019s island setting creates a unique automotive market where compact, fuel-efficient vehicles reign supreme. Limited roads and ocean-salt exposure make certified pre-owned vehicles essential, as buyers need assurance of thorough inspection and quality. Toyota and Honda dominate for their legendary reliability, while growing EV adoption reflects Hawaii\u2019s commitment to renewable energy and sustainable island living.',
        seo: {
          title: 'Used Cars in Honolulu, HI | BESPOKIAN Auto',
          description:
            'Browse 287 certified pre-owned vehicles for delivery to Honolulu. Every car inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Idaho ---- */
  {
    slug: 'idaho',
    name: 'Idaho',
    abbreviation: 'ID',
    salesTaxRate: 6,
    registrationFee: 6900,
    titleFee: 1400,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Toyota', 'Chevrolet', 'Subaru', 'Ram'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Crossover'],
    vehicleCount: 742,
    cities: [
      {
        slug: 'boise',
        name: 'Boise',
        stateSlug: 'idaho',
        stateAbbreviation: 'ID',
        metroPopulation: 770000,
        deliveryDays: { min: 5, max: 8 },
        popularMakes: ['Subaru', 'Toyota', 'Ford', 'Jeep', 'Honda'],
        vehicleCount: 498,
        nearestHub: 'Salt Lake City Hub',
        hubDistance: 340,
        localTaxAddon: 0,
        description:
          'Boise has emerged as one of America\u2019s hottest relocation destinations, and its automotive market reflects this rapid growth. Newcomers from California and the Pacific Northwest bring a preference for Subaru and Toyota crossovers ideal for Idaho\u2019s four-season outdoor lifestyle. The city\u2019s proximity to world-class skiing and hiking makes all-wheel-drive capability a virtual requirement among local buyers.',
        seo: {
          title: 'Used Cars in Boise, ID | BESPOKIAN Auto',
          description:
            'Browse 498 certified pre-owned vehicles in Boise. Adventure-ready SUVs and crossovers, inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Illinois ---- */
  {
    slug: 'illinois',
    name: 'Illinois',
    abbreviation: 'IL',
    salesTaxRate: 6.25,
    registrationFee: 15100,
    titleFee: 15000,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 4962,
    cities: [
      {
        slug: 'chicago',
        name: 'Chicago',
        stateSlug: 'illinois',
        stateAbbreviation: 'IL',
        metroPopulation: 9459000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['BMW', 'Toyota', 'Honda', 'Mercedes-Benz', 'Tesla'],
        vehicleCount: 3421,
        nearestHub: 'Chicago Hub',
        hubDistance: 0,
        localTaxAddon: 4.75,
        description:
          'Chicago\u2019s world-class economy and four-season climate create one of America\u2019s most diverse automotive markets. Harsh winters demand capable all-wheel-drive vehicles, while summer along Lake Michigan calls for convertibles and sport sedans. The Windy City\u2019s sophisticated buyers favor German luxury and Japanese reliability in nearly equal measure, making certified pre-owned the intelligent choice for navigating urban streets.',
        seo: {
          title: 'Used Cars in Chicago, IL | BESPOKIAN Auto',
          description:
            'Browse 3,421 certified pre-owned vehicles in Chicago. From luxury sedans to winter-ready SUVs, backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Indiana ---- */
  {
    slug: 'indiana',
    name: 'Indiana',
    abbreviation: 'IN',
    salesTaxRate: 7,
    registrationFee: 2155,
    titleFee: 1500,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Honda', 'Ram'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 2186,
    cities: [
      {
        slug: 'indianapolis',
        name: 'Indianapolis',
        stateSlug: 'indiana',
        stateAbbreviation: 'IN',
        metroPopulation: 2111000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Honda', 'Ram'],
        vehicleCount: 1432,
        nearestHub: 'Chicago Hub',
        hubDistance: 184,
        localTaxAddon: 0,
        description:
          'Indianapolis, the Crossroads of America, lives up to its name with a car culture rooted in racing heritage and Midwestern pragmatism. Home to the Indy 500, residents appreciate performance but demand value and dependability for daily driving. The metro\u2019s affordable living costs allow buyers to invest in premium certified pre-owned vehicles that deliver an elevated ownership experience at sensible prices.',
        seo: {
          title: 'Used Cars in Indianapolis, IN | BESPOKIAN Auto',
          description:
            'Browse 1,432 certified pre-owned vehicles in Indianapolis. From trucks to sedans, every vehicle inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Iowa ---- */
  {
    slug: 'iowa',
    name: 'Iowa',
    abbreviation: 'IA',
    salesTaxRate: 6,
    registrationFee: 0,
    titleFee: 2500,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Ram', 'Honda'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 1042,
    cities: [],
  },
  /* ---- Kansas ---- */
  {
    slug: 'kansas',
    name: 'Kansas',
    abbreviation: 'KS',
    salesTaxRate: 6.5,
    registrationFee: 3900,
    titleFee: 1100,
    emissionsTest: false,
    safetyInspection: true,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Ram', 'GMC'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 1124,
    cities: [
      {
        slug: 'kansas-city',
        name: 'Kansas City',
        stateSlug: 'kansas',
        stateAbbreviation: 'KS',
        metroPopulation: 2192000,
        deliveryDays: { min: 5, max: 8 },
        popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Honda', 'GMC'],
        vehicleCount: 874,
        nearestHub: 'Kansas City Hub',
        hubDistance: 0,
        localTaxAddon: 3.125,
        description:
          'Kansas City straddles two states and offers a distinctive automotive market shaped by Midwestern values and barbecue-trail road trips. The metro\u2019s strong manufacturing heritage fuels loyalty to American brands, while a growing tech sector brings demand for premium imports. Wide-open highways and affordable parking mean Kansas City buyers prioritize comfort and capability in their certified pre-owned vehicles.',
        seo: {
          title: 'Used Cars in Kansas City, KS | BESPOKIAN Auto',
          description:
            'Browse 874 certified pre-owned vehicles in Kansas City. Every vehicle inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Kentucky ---- */
  {
    slug: 'kentucky',
    name: 'Kentucky',
    abbreviation: 'KY',
    salesTaxRate: 6,
    registrationFee: 2100,
    titleFee: 900,
    emissionsTest: false,
    safetyInspection: true,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Toyota', 'Chevrolet', 'Honda', 'Ram'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 1356,
    cities: [
      {
        slug: 'louisville',
        name: 'Louisville',
        stateSlug: 'kentucky',
        stateAbbreviation: 'KY',
        metroPopulation: 1285000,
        deliveryDays: { min: 5, max: 8 },
        popularMakes: ['Ford', 'Toyota', 'Chevrolet', 'Honda', 'Lexus'],
        vehicleCount: 812,
        nearestHub: 'Chicago Hub',
        hubDistance: 290,
        localTaxAddon: 0,
        description:
          'Louisville\u2019s bourbon heritage and Derby traditions reflect a city that appreciates timeless quality and craftsmanship. The local automotive market mirrors this ethos, with buyers seeking well-maintained, premium pre-owned vehicles that deliver lasting value. Ford\u2019s local manufacturing presence creates strong brand loyalty, while Toyota\u2019s Georgetown plant drives appreciation for Japanese engineering among Louisville\u2019s practical but quality-minded drivers.',
        seo: {
          title: 'Used Cars in Louisville, KY | BESPOKIAN Auto',
          description:
            'Browse 812 certified pre-owned vehicles in Louisville. Quality craftsmanship inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Louisiana ---- */
  {
    slug: 'louisiana',
    name: 'Louisiana',
    abbreviation: 'LA',
    salesTaxRate: 4.45,
    registrationFee: 2000,
    titleFee: 6850,
    emissionsTest: false,
    safetyInspection: true,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Ram', 'Nissan'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 1587,
    cities: [
      {
        slug: 'new-orleans',
        name: 'New Orleans',
        stateSlug: 'louisiana',
        stateAbbreviation: 'LA',
        metroPopulation: 1271000,
        deliveryDays: { min: 5, max: 8 },
        popularMakes: ['Toyota', 'Ford', 'Chevrolet', 'Honda', 'Nissan'],
        vehicleCount: 756,
        nearestHub: 'Houston Hub',
        hubDistance: 348,
        localTaxAddon: 5,
        description:
          'New Orleans\u2019 vibrant culture and unique geography shape an automotive market where practical reliability meets Southern style. Elevated roads and occasional flooding make certified inspections particularly valuable for local buyers. The Crescent City\u2019s compact historic core contrasts with sprawling suburban parishes, creating demand for both efficient city cars and capable family SUVs among NOLA\u2019s distinctive and discerning residents.',
        seo: {
          title: 'Used Cars in New Orleans, LA | BESPOKIAN Auto',
          description:
            'Browse 756 certified pre-owned vehicles in New Orleans. Thoroughly inspected and delivered with our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Maine ---- */
  {
    slug: 'maine',
    name: 'Maine',
    abbreviation: 'ME',
    salesTaxRate: 5.5,
    registrationFee: 3500,
    titleFee: 3300,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 5, max: 10 },
    popularMakes: ['Toyota', 'Subaru', 'Ford', 'Honda', 'Chevrolet'],
    popularBodyTypes: ['SUV', 'Pickup Truck', 'Crossover'],
    vehicleCount: 612,
    cities: [],
  },
  /* ---- Maryland ---- */
  {
    slug: 'maryland',
    name: 'Maryland',
    abbreviation: 'MD',
    salesTaxRate: 6,
    registrationFee: 13500,
    titleFee: 10000,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Toyota', 'Honda', 'BMW', 'Mercedes-Benz', 'Ford'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 2386,
    cities: [
      {
        slug: 'baltimore',
        name: 'Baltimore',
        stateSlug: 'maryland',
        stateAbbreviation: 'MD',
        metroPopulation: 2800000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Toyota', 'Honda', 'BMW', 'Mercedes-Benz', 'Acura'],
        vehicleCount: 1456,
        nearestHub: 'Washington DC Hub',
        hubDistance: 40,
        localTaxAddon: 0,
        description:
          'Baltimore\u2019s revitalized waterfront and thriving medical corridor attract professionals who seek premium certified pre-owned vehicles with proven reliability. Charm City\u2019s proximity to Washington DC creates a sophisticated buyer base familiar with luxury brands yet practical enough to demand certification and warranties. The metro\u2019s four-season climate makes all-wheel-drive options particularly popular among Baltimore\u2019s discerning commuters.',
        seo: {
          title: 'Used Cars in Baltimore, MD | BESPOKIAN Auto',
          description:
            'Browse 1,456 certified pre-owned vehicles in Baltimore. Premium quality inspected and delivered with our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Massachusetts ---- */
  {
    slug: 'massachusetts',
    name: 'Massachusetts',
    abbreviation: 'MA',
    salesTaxRate: 6.25,
    registrationFee: 7500,
    titleFee: 7500,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Toyota', 'Honda', 'Subaru', 'BMW', 'Audi'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 2687,
    cities: [
      {
        slug: 'boston',
        name: 'Boston',
        stateSlug: 'massachusetts',
        stateAbbreviation: 'MA',
        metroPopulation: 4899000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['BMW', 'Toyota', 'Audi', 'Subaru', 'Tesla'],
        vehicleCount: 1834,
        nearestHub: 'Boston Hub',
        hubDistance: 0,
        localTaxAddon: 0,
        description:
          'Boston\u2019s intellectual heritage and fierce New England winters shape one of the country\u2019s most demanding automotive markets. The city\u2019s educated buyer base conducts thorough research before purchasing, making certified pre-owned with transparent inspection reports the natural choice. From navigating Storrow Drive to weekend escapes to Cape Cod, Boston drivers need versatile vehicles that handle any condition with confidence.',
        seo: {
          title: 'Used Cars in Boston, MA | BESPOKIAN Auto',
          description:
            'Browse 1,834 certified pre-owned vehicles in Boston. Winter-ready luxury inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Michigan ---- */
  {
    slug: 'michigan',
    name: 'Michigan',
    abbreviation: 'MI',
    salesTaxRate: 6,
    registrationFee: 0,
    titleFee: 1500,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Ford', 'Chevrolet', 'GMC', 'Ram', 'Toyota'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 3245,
    cities: [
      {
        slug: 'detroit',
        name: 'Detroit',
        stateSlug: 'michigan',
        stateAbbreviation: 'MI',
        metroPopulation: 4393000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Ford', 'Chevrolet', 'GMC', 'Ram', 'Jeep'],
        vehicleCount: 2187,
        nearestHub: 'Detroit Hub',
        hubDistance: 0,
        localTaxAddon: 0,
        description:
          'Detroit is the birthplace of the American automobile, and that heritage runs deep in the Motor City\u2019s automotive DNA. Domestic brands command fierce loyalty, with Ford, Chevrolet, and Ram leading every segment. Yet the city\u2019s automotive professionals also appreciate engineering excellence regardless of origin. Certified pre-owned vehicles here benefit from a buyer base that truly understands what quality looks like under the hood.',
        seo: {
          title: 'Used Cars in Detroit, MI | BESPOKIAN Auto',
          description:
            'Browse 2,187 certified pre-owned vehicles in the Motor City. American automotive heritage, inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Minnesota ---- */
  {
    slug: 'minnesota',
    name: 'Minnesota',
    abbreviation: 'MN',
    salesTaxRate: 6.875,
    registrationFee: 0,
    titleFee: 850,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Toyota', 'Ford', 'Chevrolet', 'Honda', 'Subaru'],
    popularBodyTypes: ['SUV', 'Pickup Truck', 'Crossover'],
    vehicleCount: 2342,
    cities: [
      {
        slug: 'minneapolis',
        name: 'Minneapolis',
        stateSlug: 'minnesota',
        stateAbbreviation: 'MN',
        metroPopulation: 3690000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Toyota', 'Subaru', 'Honda', 'BMW', 'Tesla'],
        vehicleCount: 1678,
        nearestHub: 'Chicago Hub',
        hubDistance: 410,
        localTaxAddon: 0.5,
        description:
          'Minneapolis blends Scandinavian-influenced design sensibility with practical cold-weather needs in its automotive preferences. The Twin Cities\u2019 extreme winters make all-wheel-drive practically mandatory, while the metro\u2019s environmental consciousness drives strong Tesla and hybrid demand. Known for its chain of lakes and vibrant arts scene, Minneapolis buyers seek vehicles that are as thoughtfully designed as they are capable and certified.',
        seo: {
          title: 'Used Cars in Minneapolis, MN | BESPOKIAN Auto',
          description:
            'Browse 1,678 certified pre-owned vehicles in Minneapolis. Winter-ready AWD vehicles inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Mississippi ---- */
  {
    slug: 'mississippi',
    name: 'Mississippi',
    abbreviation: 'MS',
    salesTaxRate: 5,
    registrationFee: 1400,
    titleFee: 900,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Nissan', 'Ram'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 842,
    cities: [],
  },
  /* ---- Missouri ---- */
  {
    slug: 'missouri',
    name: 'Missouri',
    abbreviation: 'MO',
    salesTaxRate: 4.225,
    registrationFee: 5100,
    titleFee: 1100,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Ram', 'Honda'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 2156,
    cities: [
      {
        slug: 'st-louis',
        name: 'St. Louis',
        stateSlug: 'missouri',
        stateAbbreviation: 'MO',
        metroPopulation: 2807000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Honda', 'BMW'],
        vehicleCount: 1287,
        nearestHub: 'Chicago Hub',
        hubDistance: 297,
        localTaxAddon: 4.454,
        description:
          'St. Louis\u2019s Gateway Arch symbolizes a city that bridges East and West, and its automotive market reflects that same diversity. A strong corporate presence and affordable cost of living allow buyers to invest in quality certified pre-owned vehicles. The metro\u2019s variable weather demands year-round versatility, while its Midwestern roots ensure buyers prioritize substance and lasting value over fleeting automotive trends.',
        seo: {
          title: 'Used Cars in St. Louis, MO | BESPOKIAN Auto',
          description:
            'Browse 1,287 certified pre-owned vehicles in St. Louis. Every vehicle inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Montana ---- */
  {
    slug: 'montana',
    name: 'Montana',
    abbreviation: 'MT',
    salesTaxRate: 0,
    registrationFee: 0,
    titleFee: 1200,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 6, max: 10 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Ram', 'Subaru'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Crossover'],
    vehicleCount: 412,
    cities: [],
  },
  /* ---- Nebraska ---- */
  {
    slug: 'nebraska',
    name: 'Nebraska',
    abbreviation: 'NE',
    salesTaxRate: 5.5,
    registrationFee: 1500,
    titleFee: 1000,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Ram', 'GMC'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 756,
    cities: [],
  },
  /* ---- Nevada ---- */
  {
    slug: 'nevada',
    name: 'Nevada',
    abbreviation: 'NV',
    salesTaxRate: 6.85,
    registrationFee: 3300,
    titleFee: 2850,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Toyota', 'Ford', 'Chevrolet', 'Honda', 'Tesla'],
    popularBodyTypes: ['SUV', 'Sedan', 'Pickup Truck'],
    vehicleCount: 1834,
    cities: [
      {
        slug: 'las-vegas',
        name: 'Las Vegas',
        stateSlug: 'nevada',
        stateAbbreviation: 'NV',
        metroPopulation: 2265000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Toyota', 'Ford', 'Tesla', 'BMW', 'Mercedes-Benz'],
        vehicleCount: 1342,
        nearestHub: 'Las Vegas Hub',
        hubDistance: 0,
        localTaxAddon: 1.525,
        description:
          'Las Vegas\u2019s entertainment economy and hospitality workforce create a 24-hour automotive market unlike any other. The desert city\u2019s extreme heat tests vehicles rigorously, making certified pre-owned with thorough inspection essential. Luxury brands thrive among Vegas\u2019s tourism professionals and entertainment executives, while the city\u2019s growing tech corridor brings demand for Tesla and premium crossovers among its newest residents.',
        seo: {
          title: 'Used Cars in Las Vegas, NV | BESPOKIAN Auto',
          description:
            'Browse 1,342 certified pre-owned vehicles in Las Vegas. Desert-tested quality inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- New Hampshire ---- */
  {
    slug: 'new-hampshire',
    name: 'New Hampshire',
    abbreviation: 'NH',
    salesTaxRate: 0,
    registrationFee: 3600,
    titleFee: 2500,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Toyota', 'Subaru', 'Honda', 'Ford', 'Jeep'],
    popularBodyTypes: ['SUV', 'Crossover', 'Pickup Truck'],
    vehicleCount: 624,
    cities: [],
  },
  /* ---- New Jersey ---- */
  {
    slug: 'new-jersey',
    name: 'New Jersey',
    abbreviation: 'NJ',
    salesTaxRate: 6.625,
    registrationFee: 3500,
    titleFee: 6000,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Toyota', 'Honda', 'BMW', 'Mercedes-Benz', 'Lexus'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 3124,
    cities: [
      {
        slug: 'newark',
        name: 'Newark',
        stateSlug: 'new-jersey',
        stateAbbreviation: 'NJ',
        metroPopulation: 2050000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['BMW', 'Mercedes-Benz', 'Toyota', 'Honda', 'Acura'],
        vehicleCount: 1245,
        nearestHub: 'New York Hub',
        hubDistance: 14,
        localTaxAddon: 0,
        description:
          'Newark and northern New Jersey offer a compelling alternative to New York City\u2019s automotive market, with easier logistics and significant savings. The Gateway City\u2019s proximity to Manhattan attracts luxury buyers who want premium vehicles without the city\u2019s premium pricing. Strong demand for BMW and Mercedes reflects the area\u2019s affluent commuter communities, where certified pre-owned quality meets practical suburban value.',
        seo: {
          title: 'Used Cars in Newark, NJ | BESPOKIAN Auto',
          description:
            'Browse 1,245 certified pre-owned vehicles in Newark. Premium brands inspected and delivered with our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- New Mexico ---- */
  {
    slug: 'new-mexico',
    name: 'New Mexico',
    abbreviation: 'NM',
    salesTaxRate: 4,
    registrationFee: 2700,
    titleFee: 500,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Toyota', 'Chevrolet', 'Ram', 'Jeep'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Crossover'],
    vehicleCount: 687,
    cities: [
      {
        slug: 'albuquerque',
        name: 'Albuquerque',
        stateSlug: 'new-mexico',
        stateAbbreviation: 'NM',
        metroPopulation: 916000,
        deliveryDays: { min: 5, max: 8 },
        popularMakes: ['Toyota', 'Ford', 'Chevrolet', 'Subaru', 'Honda'],
        vehicleCount: 478,
        nearestHub: 'Phoenix Hub',
        hubDistance: 450,
        localTaxAddon: 3.9375,
        description:
          'Albuquerque\u2019s high-desert setting and artistic spirit create an automotive market that values rugged capability and understated style. The Duke City\u2019s elevation and arid climate preserve vehicles beautifully, while long distances between destinations make reliable transportation essential. Buyers here appreciate the transparency of certified pre-owned vehicles, combining Southwest independence with smart, informed purchasing decisions.',
        seo: {
          title: 'Used Cars in Albuquerque, NM | BESPOKIAN Auto',
          description:
            'Browse 478 certified pre-owned vehicles in Albuquerque. Desert-preserved quality backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- New York ---- */
  {
    slug: 'new-york',
    name: 'New York',
    abbreviation: 'NY',
    salesTaxRate: 4,
    registrationFee: 2625,
    titleFee: 5000,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Toyota', 'Honda', 'BMW', 'Mercedes-Benz', 'Lexus'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 5678,
    cities: [
      {
        slug: 'new-york-city',
        name: 'New York City',
        stateSlug: 'new-york',
        stateAbbreviation: 'NY',
        metroPopulation: 20140000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['BMW', 'Mercedes-Benz', 'Tesla', 'Lexus', 'Audi'],
        vehicleCount: 3246,
        nearestHub: 'New York Hub',
        hubDistance: 0,
        localTaxAddon: 4.5,
        description:
          'New York City is the ultimate automotive paradox: the world\u2019s most walkable city with an insatiable appetite for premium vehicles. From Manhattan\u2019s luxury garages to Brooklyn\u2019s brownstone-lined streets, New Yorkers who choose to own vehicles demand the very best. Certified pre-owned luxury here isn\u2019t just transportation\u2014it\u2019s a statement of discerning taste in a city that refuses to compromise on quality.',
        seo: {
          title: 'Used Cars in New York City, NY | BESPOKIAN Auto',
          description:
            'Browse 3,246 certified pre-owned luxury vehicles in NYC. Premium brands inspected and delivered with our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- North Carolina ---- */
  {
    slug: 'north-carolina',
    name: 'North Carolina',
    abbreviation: 'NC',
    salesTaxRate: 3,
    registrationFee: 3800,
    titleFee: 5600,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Toyota', 'Ford', 'Honda', 'Chevrolet', 'BMW'],
    popularBodyTypes: ['SUV', 'Sedan', 'Pickup Truck'],
    vehicleCount: 3564,
    cities: [
      {
        slug: 'charlotte',
        name: 'Charlotte',
        stateSlug: 'north-carolina',
        stateAbbreviation: 'NC',
        metroPopulation: 2660000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['BMW', 'Toyota', 'Mercedes-Benz', 'Honda', 'Ford'],
        vehicleCount: 1687,
        nearestHub: 'Atlanta Hub',
        hubDistance: 244,
        localTaxAddon: 0,
        description:
          'Charlotte\u2019s banking industry and NASCAR heritage create a fascinating blend of financial sophistication and racing passion. The Queen City\u2019s explosive growth has made it the Southeast\u2019s second-largest banking center, attracting professionals who favor premium German luxury. Meanwhile, the region\u2019s motorsport culture ensures performance vehicles maintain strong demand in this dynamic and rapidly expanding Southern metro.',
        seo: {
          title: 'Used Cars in Charlotte, NC | BESPOKIAN Auto',
          description:
            'Browse 1,687 certified pre-owned vehicles in Charlotte. Premium quality inspected and backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'raleigh',
        name: 'Raleigh',
        stateSlug: 'north-carolina',
        stateAbbreviation: 'NC',
        metroPopulation: 1413000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Toyota', 'Honda', 'Subaru', 'Tesla', 'BMW'],
        vehicleCount: 1124,
        nearestHub: 'Atlanta Hub',
        hubDistance: 406,
        localTaxAddon: 0,
        description:
          'Raleigh\u2019s Research Triangle Park fuels an automotive market driven by technology professionals who approach car buying with analytical precision. The Triangle\u2019s educated buyer base researches extensively and values transparency, making certified pre-owned vehicles the logical choice. Strong demand for Tesla and Subaru reflects the area\u2019s blend of environmental awareness and practical all-weather capability for North Carolina\u2019s mild but variable climate.',
        seo: {
          title: 'Used Cars in Raleigh, NC | BESPOKIAN Auto',
          description:
            'Browse 1,124 certified pre-owned vehicles in Raleigh-Durham. Every vehicle inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- North Dakota ---- */
  {
    slug: 'north-dakota',
    name: 'North Dakota',
    abbreviation: 'ND',
    salesTaxRate: 5,
    registrationFee: 4900,
    titleFee: 500,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 6, max: 10 },
    popularMakes: ['Ford', 'Chevrolet', 'Ram', 'Toyota', 'GMC'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 312,
    cities: [],
  },
  /* ---- Ohio ---- */
  {
    slug: 'ohio',
    name: 'Ohio',
    abbreviation: 'OH',
    salesTaxRate: 5.75,
    registrationFee: 3150,
    titleFee: 1500,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Honda', 'Ram'],
    popularBodyTypes: ['SUV', 'Pickup Truck', 'Sedan'],
    vehicleCount: 3687,
    cities: [
      {
        slug: 'columbus',
        name: 'Columbus',
        stateSlug: 'ohio',
        stateAbbreviation: 'OH',
        metroPopulation: 2138000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Honda', 'Toyota', 'Ford', 'Chevrolet', 'BMW'],
        vehicleCount: 1356,
        nearestHub: 'Detroit Hub',
        hubDistance: 200,
        localTaxAddon: 2,
        description:
          'Columbus is Ohio\u2019s largest and fastest-growing city, with a young, educated population that drives demand for modern, connected vehicles. Honda\u2019s massive Marysville plant nearby creates deep brand loyalty, while The Ohio State University\u2019s influence brings tech-forward preferences. The capital city\u2019s relatively flat terrain and expanding suburbs make comfortable, fuel-efficient crossovers the sweet spot for certified pre-owned buyers.',
        seo: {
          title: 'Used Cars in Columbus, OH | BESPOKIAN Auto',
          description:
            'Browse 1,356 certified pre-owned vehicles in Columbus. Every vehicle inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Oklahoma ---- */
  {
    slug: 'oklahoma',
    name: 'Oklahoma',
    abbreviation: 'OK',
    salesTaxRate: 4.5,
    registrationFee: 9100,
    titleFee: 1100,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Chevrolet', 'Ram', 'Toyota', 'GMC'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 1234,
    cities: [
      {
        slug: 'oklahoma-city',
        name: 'Oklahoma City',
        stateSlug: 'oklahoma',
        stateAbbreviation: 'OK',
        metroPopulation: 1425000,
        deliveryDays: { min: 5, max: 8 },
        popularMakes: ['Ford', 'Chevrolet', 'Ram', 'Toyota', 'GMC'],
        vehicleCount: 834,
        nearestHub: 'Dallas Hub',
        hubDistance: 206,
        localTaxAddon: 4.125,
        description:
          'Oklahoma City\u2019s energy industry roots and Western heritage shape an automotive market where trucks and SUVs reign supreme. The metro\u2019s rapid downtown revitalization has brought younger professionals seeking premium options beyond traditional domestics. OKC\u2019s exceptionally affordable cost of living means buyers can stretch further into certified pre-owned luxury, getting more vehicle and more assurance for their investment.',
        seo: {
          title: 'Used Cars in Oklahoma City, OK | BESPOKIAN Auto',
          description:
            'Browse 834 certified pre-owned vehicles in OKC. Trucks and SUVs inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Oregon ---- */
  {
    slug: 'oregon',
    name: 'Oregon',
    abbreviation: 'OR',
    salesTaxRate: 0,
    registrationFee: 13200,
    titleFee: 7730,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Subaru', 'Toyota', 'Honda', 'Tesla', 'Ford'],
    popularBodyTypes: ['SUV', 'Crossover', 'Wagon'],
    vehicleCount: 1756,
    cities: [
      {
        slug: 'portland',
        name: 'Portland',
        stateSlug: 'oregon',
        stateAbbreviation: 'OR',
        metroPopulation: 2512000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Subaru', 'Tesla', 'Toyota', 'Honda', 'Rivian'],
        vehicleCount: 1287,
        nearestHub: 'Seattle Hub',
        hubDistance: 174,
        localTaxAddon: 0.5,
        description:
          'Portland\u2019s fiercely independent, eco-conscious culture creates one of America\u2019s most distinctive automotive markets. Oregon\u2019s zero sales tax makes it a magnet for vehicle purchases, while the city\u2019s environmental values drive exceptional adoption of electric and hybrid vehicles. Subaru is practically the official car of Portland, and the city\u2019s proximity to both mountains and coast demands versatile all-weather capability from every vehicle.',
        seo: {
          title: 'Used Cars in Portland, OR | BESPOKIAN Auto',
          description:
            'Browse 1,287 certified pre-owned vehicles in Portland. Zero sales tax, every vehicle inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Pennsylvania ---- */
  {
    slug: 'pennsylvania',
    name: 'Pennsylvania',
    abbreviation: 'PA',
    salesTaxRate: 6,
    registrationFee: 3900,
    titleFee: 5300,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Toyota', 'Ford', 'Honda', 'Chevrolet', 'BMW'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 4124,
    cities: [
      {
        slug: 'philadelphia',
        name: 'Philadelphia',
        stateSlug: 'pennsylvania',
        stateAbbreviation: 'PA',
        metroPopulation: 6246000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['BMW', 'Toyota', 'Honda', 'Mercedes-Benz', 'Audi'],
        vehicleCount: 2345,
        nearestHub: 'Philadelphia Hub',
        hubDistance: 0,
        localTaxAddon: 2,
        description:
          'Philadelphia\u2019s storied history and modern renaissance have created an automotive market that values both heritage and innovation. The City of Brotherly Love\u2019s Main Line suburbs anchor the luxury segment, while the city\u2019s growing tech and medical corridors attract premium crossover buyers. Narrow city streets demand nimble handling, while suburban commutes favor comfort, making certified pre-owned versatility essential for Philly drivers.',
        seo: {
          title: 'Used Cars in Philadelphia, PA | BESPOKIAN Auto',
          description:
            'Browse 2,345 certified pre-owned vehicles in Philadelphia. Premium quality inspected and backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'pittsburgh',
        name: 'Pittsburgh',
        stateSlug: 'pennsylvania',
        stateAbbreviation: 'PA',
        metroPopulation: 2370000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Toyota', 'Ford', 'Chevrolet', 'Subaru', 'Honda'],
        vehicleCount: 1234,
        nearestHub: 'Philadelphia Hub',
        hubDistance: 305,
        localTaxAddon: 1,
        description:
          'Pittsburgh\u2019s transformation from Steel City to tech hub is mirrored in its evolving automotive market. Carnegie Mellon\u2019s autonomous vehicle research puts Pittsburgh at the forefront of automotive innovation, while the city\u2019s hilly terrain and harsh winters demand capable all-wheel-drive vehicles. Subaru\u2019s strong presence alongside traditional American brands reflects a city that honors its working-class roots while embracing a high-tech future.',
        seo: {
          title: 'Used Cars in Pittsburgh, PA | BESPOKIAN Auto',
          description:
            'Browse 1,234 certified pre-owned vehicles in Pittsburgh. Winter-ready quality inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Rhode Island ---- */
  {
    slug: 'rhode-island',
    name: 'Rhode Island',
    abbreviation: 'RI',
    salesTaxRate: 7,
    registrationFee: 3500,
    titleFee: 5075,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Toyota', 'Honda', 'Ford', 'Subaru', 'BMW'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 387,
    cities: [],
  },
  /* ---- South Carolina ---- */
  {
    slug: 'south-carolina',
    name: 'South Carolina',
    abbreviation: 'SC',
    salesTaxRate: 6,
    registrationFee: 4000,
    titleFee: 1500,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Ford', 'Toyota', 'BMW', 'Chevrolet', 'Honda'],
    popularBodyTypes: ['SUV', 'Pickup Truck', 'Sedan'],
    vehicleCount: 1678,
    cities: [],
  },
  /* ---- South Dakota ---- */
  {
    slug: 'south-dakota',
    name: 'South Dakota',
    abbreviation: 'SD',
    salesTaxRate: 4.5,
    registrationFee: 0,
    titleFee: 1000,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 6, max: 10 },
    popularMakes: ['Ford', 'Chevrolet', 'Ram', 'Toyota', 'GMC'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 287,
    cities: [],
  },
  /* ---- Tennessee ---- */
  {
    slug: 'tennessee',
    name: 'Tennessee',
    abbreviation: 'TN',
    salesTaxRate: 7,
    registrationFee: 2675,
    titleFee: 1100,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Ford', 'Toyota', 'Chevrolet', 'Honda', 'Nissan'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 2876,
    cities: [
      {
        slug: 'nashville',
        name: 'Nashville',
        stateSlug: 'tennessee',
        stateAbbreviation: 'TN',
        metroPopulation: 1989000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Toyota', 'Ford', 'BMW', 'Chevrolet', 'Tesla'],
        vehicleCount: 1456,
        nearestHub: 'Atlanta Hub',
        hubDistance: 249,
        localTaxAddon: 2.25,
        description:
          'Nashville\u2019s explosive growth as Music City USA has transformed its automotive landscape from country trucks to a sophisticated mix of luxury and utility. The city\u2019s booming entertainment, healthcare, and tech sectors attract affluent young professionals seeking premium certified pre-owned vehicles. Nashville\u2019s suburban sprawl means significant daily commutes, making comfortable, well-equipped SUVs and crossovers the fastest-moving segment in this vibrant market.',
        seo: {
          title: 'Used Cars in Nashville, TN | BESPOKIAN Auto',
          description:
            'Browse 1,456 certified pre-owned vehicles in Nashville. Premium quality inspected and backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'memphis',
        name: 'Memphis',
        stateSlug: 'tennessee',
        stateAbbreviation: 'TN',
        metroPopulation: 1340000,
        deliveryDays: { min: 5, max: 8 },
        popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Nissan', 'Ram'],
        vehicleCount: 867,
        nearestHub: 'Atlanta Hub',
        hubDistance: 390,
        localTaxAddon: 2.25,
        description:
          'Memphis\u2019s blues heritage and logistics dominance\u2014as home to FedEx\u2019s global hub\u2014create a pragmatic yet soulful automotive market. The Bluff City\u2019s workforce values reliable transportation for long shifts and commutes, while the growing medical corridor attracts professionals seeking certified luxury. Memphis buyers appreciate honest value and transparent quality, making BESPOKIAN\u2019s certified approach a natural fit for this authentic Southern city.',
        seo: {
          title: 'Used Cars in Memphis, TN | BESPOKIAN Auto',
          description:
            'Browse 867 certified pre-owned vehicles in Memphis. Every vehicle inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Texas ---- */
  {
    slug: 'texas',
    name: 'Texas',
    abbreviation: 'TX',
    salesTaxRate: 6.25,
    registrationFee: 5075,
    titleFee: 3300,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Ram', 'Honda'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 7856,
    cities: [
      {
        slug: 'houston',
        name: 'Houston',
        stateSlug: 'texas',
        stateAbbreviation: 'TX',
        metroPopulation: 7122000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Ford', 'Toyota', 'Chevrolet', 'Ram', 'Mercedes-Benz'],
        vehicleCount: 2876,
        nearestHub: 'Houston Hub',
        hubDistance: 0,
        localTaxAddon: 2,
        description:
          'Houston\u2019s energy economy and international diversity create America\u2019s most robust truck and SUV market. Space City\u2019s vast metropolitan sprawl means vehicles are essential, and full-size trucks are almost a cultural requirement. Yet Houston\u2019s growing medical center and tech sectors bring increasing demand for premium luxury vehicles, making the certified pre-owned market here as diverse and ambitious as the city itself.',
        seo: {
          title: 'Used Cars in Houston, TX | BESPOKIAN Auto',
          description:
            'Browse 2,876 certified pre-owned vehicles in Houston. From trucks to luxury sedans, inspected and backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'dallas',
        name: 'Dallas',
        stateSlug: 'texas',
        stateAbbreviation: 'TX',
        metroPopulation: 7637000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Ford', 'Toyota', 'BMW', 'Mercedes-Benz', 'Ram'],
        vehicleCount: 2654,
        nearestHub: 'Dallas Hub',
        hubDistance: 0,
        localTaxAddon: 2,
        description:
          'Dallas epitomizes Texas-sized ambition, and its automotive market mirrors that boldness with strong demand across every premium segment. Big D\u2019s corporate headquarters concentration drives executive luxury purchases, while the surrounding suburban sprawl fuels truck and SUV sales. From Park Cities elegance to Plano\u2019s family-focused crossovers, certified pre-owned vehicles in Dallas cater to every facet of this prosperous metroplex.',
        seo: {
          title: 'Used Cars in Dallas, TX | BESPOKIAN Auto',
          description:
            'Browse 2,654 certified pre-owned vehicles in Dallas. Premium brands inspected and backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'san-antonio',
        name: 'San Antonio',
        stateSlug: 'texas',
        stateAbbreviation: 'TX',
        metroPopulation: 2558000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Ford', 'Toyota', 'Chevrolet', 'Ram', 'Honda'],
        vehicleCount: 1234,
        nearestHub: 'Houston Hub',
        hubDistance: 197,
        localTaxAddon: 2,
        description:
          'San Antonio\u2019s rich military heritage and growing tech sector create an automotive market that values both rugged capability and modern efficiency. Joint Base San Antonio brings a steady stream of buyers who appreciate transparent pricing and certified quality. The Alamo City\u2019s affordable lifestyle allows residents to invest in well-equipped trucks and SUVs, making it one of Texas\u2019s strongest certified pre-owned markets.',
        seo: {
          title: 'Used Cars in San Antonio, TX | BESPOKIAN Auto',
          description:
            'Browse 1,234 certified pre-owned vehicles in San Antonio. Every vehicle inspected and backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'austin',
        name: 'Austin',
        stateSlug: 'texas',
        stateAbbreviation: 'TX',
        metroPopulation: 2283000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Tesla', 'Toyota', 'Ford', 'BMW', 'Subaru'],
        vehicleCount: 1092,
        nearestHub: 'Houston Hub',
        hubDistance: 162,
        localTaxAddon: 2,
        description:
          'Austin\u2019s tech boom and live-music culture make it Texas\u2019s most unconventional automotive market. Tesla\u2019s Gigafactory has electrified the local market, literally and figuratively, while the Keep Austin Weird ethos extends to eclectic vehicle choices. Young tech professionals demand EV options and connected features, while the surrounding Hill Country drives demand for capable crossovers suited to scenic weekend drives along Texas wine country.',
        seo: {
          title: 'Used Cars in Austin, TX | BESPOKIAN Auto',
          description:
            'Browse 1,092 certified pre-owned vehicles in Austin. Tech-forward choices inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Utah ---- */
  {
    slug: 'utah',
    name: 'Utah',
    abbreviation: 'UT',
    salesTaxRate: 6.1,
    registrationFee: 4400,
    titleFee: 600,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Toyota', 'Ford', 'Subaru', 'Chevrolet', 'Honda'],
    popularBodyTypes: ['SUV', 'Pickup Truck', 'Crossover'],
    vehicleCount: 1456,
    cities: [
      {
        slug: 'salt-lake-city',
        name: 'Salt Lake City',
        stateSlug: 'utah',
        stateAbbreviation: 'UT',
        metroPopulation: 1243000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Subaru', 'Toyota', 'Ford', 'Tesla', 'Jeep'],
        vehicleCount: 987,
        nearestHub: 'Salt Lake City Hub',
        hubDistance: 0,
        localTaxAddon: 1.1,
        description:
          'Salt Lake City\u2019s world-class ski resorts and outdoor recreation culture demand vehicles that can handle mountain passes and powder days with equal ease. The city\u2019s youthful, active population gravitates toward Subaru and Toyota for their proven all-wheel-drive reliability. Utah\u2019s growing tech corridor, the Silicon Slopes, brings Bay Area transplants who bring their Tesla preferences and premium certified pre-owned expectations.',
        seo: {
          title: 'Used Cars in Salt Lake City, UT | BESPOKIAN Auto',
          description:
            'Browse 987 certified pre-owned vehicles in Salt Lake City. Mountain-ready AWD vehicles inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Vermont ---- */
  {
    slug: 'vermont',
    name: 'Vermont',
    abbreviation: 'VT',
    salesTaxRate: 6,
    registrationFee: 7600,
    titleFee: 3500,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Subaru', 'Toyota', 'Ford', 'Honda', 'Jeep'],
    popularBodyTypes: ['SUV', 'Crossover', 'Pickup Truck'],
    vehicleCount: 287,
    cities: [],
  },
  /* ---- Virginia ---- */
  {
    slug: 'virginia',
    name: 'Virginia',
    abbreviation: 'VA',
    salesTaxRate: 4.15,
    registrationFee: 4115,
    titleFee: 1500,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 3, max: 7 },
    popularMakes: ['Toyota', 'Honda', 'Ford', 'BMW', 'Mercedes-Benz'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 3456,
    cities: [
      {
        slug: 'virginia-beach',
        name: 'Virginia Beach',
        stateSlug: 'virginia',
        stateAbbreviation: 'VA',
        metroPopulation: 1799000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Ford', 'Toyota', 'Honda', 'Chevrolet', 'Jeep'],
        vehicleCount: 987,
        nearestHub: 'Washington DC Hub',
        hubDistance: 200,
        localTaxAddon: 0,
        description:
          'Virginia Beach\u2019s military community and coastal lifestyle create a practical yet quality-conscious automotive market. Naval Station Norfolk, the world\u2019s largest naval base, generates steady demand from service members who value certified reliability and transparent pricing. Coastal living drives preference for SUVs and Jeeps suited to beach-to-base commutes, while the resort area\u2019s tourism industry supports a diverse vehicle market.',
        seo: {
          title: 'Used Cars in Virginia Beach, VA | BESPOKIAN Auto',
          description:
            'Browse 987 certified pre-owned vehicles in Virginia Beach. Every vehicle inspected and backed by our 100-Day Guarantee.',
        },
      },
      {
        slug: 'richmond',
        name: 'Richmond',
        stateSlug: 'virginia',
        stateAbbreviation: 'VA',
        metroPopulation: 1315000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Toyota', 'Honda', 'BMW', 'Mercedes-Benz', 'Ford'],
        vehicleCount: 876,
        nearestHub: 'Washington DC Hub',
        hubDistance: 109,
        localTaxAddon: 0,
        description:
          'Richmond\u2019s blend of colonial history and contemporary cool creates an automotive market that mirrors the city\u2019s dual identity. The capital\u2019s thriving craft beer scene and arts district attract young professionals who favor efficient crossovers, while the broader metro\u2019s corporate presence drives luxury sedan demand. Richmond\u2019s central I-95 location makes it a natural crossroads for certified pre-owned vehicle delivery throughout the Mid-Atlantic.',
        seo: {
          title: 'Used Cars in Richmond, VA | BESPOKIAN Auto',
          description:
            'Browse 876 certified pre-owned vehicles in Richmond. Premium quality inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Washington ---- */
  {
    slug: 'washington',
    name: 'Washington',
    abbreviation: 'WA',
    salesTaxRate: 6.5,
    registrationFee: 6580,
    titleFee: 1595,
    emissionsTest: true,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Toyota', 'Subaru', 'Honda', 'Tesla', 'Ford'],
    popularBodyTypes: ['SUV', 'Crossover', 'Sedan'],
    vehicleCount: 3245,
    cities: [
      {
        slug: 'seattle',
        name: 'Seattle',
        stateSlug: 'washington',
        stateAbbreviation: 'WA',
        metroPopulation: 4018000,
        deliveryDays: { min: 3, max: 6 },
        popularMakes: ['Tesla', 'Subaru', 'Toyota', 'BMW', 'Rivian'],
        vehicleCount: 2198,
        nearestHub: 'Seattle Hub',
        hubDistance: 0,
        localTaxAddon: 3.6,
        description:
          'Seattle\u2019s tech economy powered by Amazon, Microsoft, and Boeing creates one of the nation\u2019s wealthiest and most tech-savvy automotive markets. The Emerald City\u2019s rainy climate and outdoor culture drive exceptional demand for all-wheel-drive crossovers and electric vehicles. Tesla leads the market alongside Subaru and Rivian, reflecting Seattle buyers\u2019 blend of premium taste, environmental consciousness, and Pacific Northwest adventure readiness.',
        seo: {
          title: 'Used Cars in Seattle, WA | BESPOKIAN Auto',
          description:
            'Browse 2,198 certified pre-owned vehicles in Seattle. Tech-forward luxury inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- West Virginia ---- */
  {
    slug: 'west-virginia',
    name: 'West Virginia',
    abbreviation: 'WV',
    salesTaxRate: 6,
    registrationFee: 5150,
    titleFee: 1000,
    emissionsTest: false,
    safetyInspection: true,
    deliveryDays: { min: 5, max: 9 },
    popularMakes: ['Ford', 'Chevrolet', 'Ram', 'Toyota', 'Jeep'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Sedan'],
    vehicleCount: 542,
    cities: [],
  },
  /* ---- Wisconsin ---- */
  {
    slug: 'wisconsin',
    name: 'Wisconsin',
    abbreviation: 'WI',
    salesTaxRate: 5,
    registrationFee: 7500,
    titleFee: 16450,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 4, max: 8 },
    popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Honda', 'Ram'],
    popularBodyTypes: ['SUV', 'Pickup Truck', 'Sedan'],
    vehicleCount: 1876,
    cities: [
      {
        slug: 'milwaukee',
        name: 'Milwaukee',
        stateSlug: 'wisconsin',
        stateAbbreviation: 'WI',
        metroPopulation: 1575000,
        deliveryDays: { min: 4, max: 7 },
        popularMakes: ['Ford', 'Chevrolet', 'Toyota', 'Honda', 'BMW'],
        vehicleCount: 1124,
        nearestHub: 'Chicago Hub',
        hubDistance: 92,
        localTaxAddon: 0.5,
        description:
          'Milwaukee\u2019s proud manufacturing heritage and lakefront vitality create an automotive market that blends blue-collar practicality with growing cosmopolitan tastes. Brew City\u2019s harsh winters off Lake Michigan make all-wheel-drive capability essential, while the city\u2019s revitalized Third Ward and downtown attract premium crossover buyers. Milwaukee\u2019s affordable living and proximity to Chicago make certified pre-owned an especially attractive value proposition.',
        seo: {
          title: 'Used Cars in Milwaukee, WI | BESPOKIAN Auto',
          description:
            'Browse 1,124 certified pre-owned vehicles in Milwaukee. Winter-ready quality inspected and backed by our 100-Day Guarantee.',
        },
      },
    ],
  },
  /* ---- Wyoming ---- */
  {
    slug: 'wyoming',
    name: 'Wyoming',
    abbreviation: 'WY',
    salesTaxRate: 4,
    registrationFee: 0,
    titleFee: 1500,
    emissionsTest: false,
    safetyInspection: false,
    deliveryDays: { min: 6, max: 10 },
    popularMakes: ['Ford', 'Chevrolet', 'Ram', 'Toyota', 'Jeep'],
    popularBodyTypes: ['Pickup Truck', 'SUV', 'Crossover'],
    vehicleCount: 198,
    cities: [],
  },
  /* ---- Washington DC (treated as state for routing) ---- */
  {
    slug: 'district-of-columbia',
    name: 'District of Columbia',
    abbreviation: 'DC',
    salesTaxRate: 6,
    registrationFee: 7200,
    titleFee: 2600,
    emissionsTest: true,
    safetyInspection: true,
    deliveryDays: { min: 3, max: 6 },
    popularMakes: ['BMW', 'Mercedes-Benz', 'Tesla', 'Audi', 'Toyota'],
    popularBodyTypes: ['SUV', 'Sedan', 'Crossover'],
    vehicleCount: 1678,
    cities: [
      {
        slug: 'washington',
        name: 'Washington',
        stateSlug: 'district-of-columbia',
        stateAbbreviation: 'DC',
        metroPopulation: 6385000,
        deliveryDays: { min: 2, max: 5 },
        popularMakes: ['BMW', 'Mercedes-Benz', 'Tesla', 'Audi', 'Lexus'],
        vehicleCount: 1678,
        nearestHub: 'Washington DC Hub',
        hubDistance: 0,
        localTaxAddon: 0,
        description:
          'Washington DC\u2019s concentration of political power and global influence creates an automotive market where understated luxury meets practical sophistication. The capital\u2019s professionals favor premium German sedans and SUVs that project success without ostentation. Embassy Row\u2019s international community adds European preferences, while the region\u2019s notorious traffic makes comfortable, well-equipped certified pre-owned vehicles a daily necessity rather than a luxury.',
        seo: {
          title: 'Used Cars in Washington, DC | BESPOKIAN Auto',
          description:
            'Browse 1,678 certified pre-owned vehicles in Washington DC. Premium luxury inspected and delivered with our 100-Day Guarantee.',
        },
      },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/*  Lookup helpers                                                            */
/* -------------------------------------------------------------------------- */

export function getStateBySlug(slug: string): StateData | undefined {
  return states.find((s) => s.slug === slug);
}

export function getCityBySlug(
  stateSlug: string,
  citySlug: string,
): CityData | undefined {
  const state = getStateBySlug(stateSlug);
  if (!state) return undefined;
  return state.cities.find((c) => c.slug === citySlug);
}

export function getAllCities(): CityData[] {
  return states.flatMap((s) => s.cities);
}

export function getStatesWithCities(): StateData[] {
  return states.filter((s) => s.cities.length > 0);
}
